import { useState, useEffect, useRef, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { io } from "socket.io-client";
import { toast } from "react-toastify";
import { MapPin, Navigation, Car, Clock, DollarSign, Phone, Star, X, CheckCircle, History, ChevronRight, Bike, Truck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import MobileBottomBar from "../components/MobileBottomBar";
import API from "../api/axiosConfig";

const SOCKET_URL = (
  process.env.REACT_APP_API_URL || "http://localhost:4000/api"
).replace(/\/api\/?$/, "");

// ── Iconos Leaflet ────────────────────────────────────────────────────────────
function makeIcon(emoji, size = 32) {
  return L.divIcon({
    html: `<div style="font-size:${size}px;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3));">${emoji}</div>`,
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
  });
}

const originIcon = makeIcon("📍");
const destIcon   = makeIcon("🏁");
const ryderIcon  = makeIcon("🚗");

// ── OSRM Route Fetch ──────────────────────────────────────────────────────────
async function fetchRoute(from, to) {
  try {
    const res = await fetch(
      `https://router.project-osrm.org/route/v1/driving/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson`
    );
    const data = await res.json();
    const coords = data.routes?.[0]?.geometry?.coordinates;
    return coords ? coords.map(([lng, lat]) => [lat, lng]) : [];
  } catch {
    return [];
  }
}

// ── Auto-fit map bounds ───────────────────────────────────────────────────────
function MapFitter({ coords }) {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (fitted.current || coords.length < 2) return;
    const bounds = L.latLngBounds(coords);
    map.fitBounds(bounds, { padding: [50, 50], animate: true });
    fitted.current = true;
  }, [coords, map]);
  return null;
}

// ── Click handler para seleccionar destino en el mapa ────────────────────────
function MapClickHandler({ onMapClick }) {
  useMapEvents({ click(e) { onMapClick(e.latlng); } });
  return null;
}

// ── Búsqueda de dirección con Nominatim (con sesgo geográfico) ───────────────
async function geocodeAddress(query, biasCoords = null) {
  let url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`;
  if (biasCoords) {
    const { lat, lng } = biasCoords;
    // viewbox: lon_min,lat_max,lon_max,lat_min (~50km alrededor)
    url += `&viewbox=${lng - 0.5},${lat + 0.5},${lng + 0.5},${lat - 0.5}&bounded=0`;
  } else {
    url += `&countrycodes=ve`;
  }
  const res = await fetch(url);
  return res.json();
}

async function reverseGeocode(lat, lng) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
    );
    const data = await res.json();
    return data.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  } catch {
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  }
}

// ── Componente de estrellas ───────────────────────────────────────────────────
function StarRating({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          onClick={() => onChange(s)}
          className={`text-2xl transition-transform hover:scale-110 ${s <= value ? "text-yellow-400" : "text-gray-300"}`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

// ── Icono según vehículo ─────────────────────────────────────────────────────
function VehicleIcon({ type, size = 16 }) {
  if (type === "bicicleta") return <Bike size={size} className="text-gray-500" />;
  if (type === "camion")    return <Truck size={size} className="text-gray-500" />;
  return <Car size={size} className="text-gray-500" />;
}

const STATUS_CONFIG = {
  buscando:   { label: "Buscando conductor", color: "bg-yellow-100 text-yellow-700" },
  aceptado:   { label: "Conductor en camino", color: "bg-blue-100 text-blue-700" },
  recogiendo: { label: "Conductor llegó",     color: "bg-purple-100 text-purple-700" },
  en_viaje:   { label: "En viaje",            color: "bg-indigo-100 text-indigo-700" },
  completado: { label: "Completado",          color: "bg-green-100 text-green-700" },
  cancelado:  { label: "Cancelado",           color: "bg-red-100 text-red-700" },
};

// ── TransportPage ─────────────────────────────────────────────────────────────
export default function TransportPage() {
  const token    = localStorage.getItem("accessToken") || localStorage.getItem("token");
  const user     = (() => { try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; } })();
  const navigate = useNavigate();
  const isRyder  = user?.role === "ryder";

  const [tab, setTab] = useState("solicitar"); // "solicitar" | "historial"
  const [history, setHistory]         = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Ubicación y destino
  const [origin, setOrigin]       = useState(null); // {lat, lng, address}
  const [dest, setDest]           = useState(null);  // {lat, lng, address}
  const [destSearch, setDestSearch] = useState("");
  const [destSuggestions, setDestSuggestions] = useState([]);
  const [routeCoords, setRouteCoords] = useState([]);
  const [estimate, setEstimate]   = useState(null);
  const [vehicleType, setVehicleType] = useState("carro");
  const [paymentMethod, setPaymentMethod] = useState("efectivo");
  const [loadingEstimate, setLoadingEstimate] = useState(false);
  const [requesting, setRequesting] = useState(false);

  // Estado del viaje
  const [activeRide, setActiveRide] = useState(null);
  const [ryderInfo, setRyderInfo]   = useState(null);
  const [ryderPos, setRyderPos]     = useState(null);
  const [ryderRouteCoords, setRyderRouteCoords] = useState([]);

  // Rating
  const [showRating, setShowRating] = useState(false);
  const [ratingValue, setRatingValue] = useState(5);
  const [ratingComment, setRatingComment] = useState("");

  const socketRef = useRef(null);

  // ── Cargar historial cuando se abre el tab ───────────────────────────────
  useEffect(() => {
    if (tab !== "historial") return;
    setHistoryLoading(true);
    API.get("/transport/user/history", { params: { page: 1, limit: 30 } })
      .then((res) => setHistory(res.data?.data?.rides ?? []))
      .catch(() => toast.error("Error al cargar el historial"))
      .finally(() => setHistoryLoading(false));
  }, [tab]);

  // ── Detectar viaje activo al montar ──────────────────────────────────────
  useEffect(() => {
    API.get("/transport/active")
      .then((res) => {
        const ride = res.data?.data?.ride;
        if (ride && !["completado", "cancelado"].includes(ride.status)) {
          setActiveRide(ride);
          if (ride.ryder) setRyderInfo(ride.ryder);
        }
      })
      .catch(() => {});
  }, []);

  // ── Auto-obtener ubicación al entrar a la página ──────────────────────────
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          try {
            const address = await reverseGeocode(lat, lng);
            setOrigin({ lat, lng, address });
          } catch {
            setOrigin({ lat, lng, address: `${lat.toFixed(5)}, ${lng.toFixed(5)}` });
          }
        },
        () => {}, // silencioso si deniega permiso
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Socket.io ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const socket = io(SOCKET_URL, {
      transports: ["websocket", "polling"],
      auth: { token },
    });
    socketRef.current = socket;

    socket.on("transport:accepted", ({ ride, ryder }) => {
      setActiveRide(ride);
      setRyderInfo(ryder);
      setRyderPos({ lat: ryder.lat, lng: ryder.lng });
      toast.success(`¡${ryder.user?.name} aceptó tu viaje y va en camino!`);
    });

    socket.on("transport:ryder_location", ({ lat, lng }) => {
      setRyderPos({ lat, lng });
    });

    socket.on("transport:status_change", ({ status }) => {
      setActiveRide((prev) => prev ? { ...prev, status } : prev);
      const msgs = {
        recogiendo: "Tu conductor llegó al punto de recogida",
        en_viaje:   "¡Estás en camino a tu destino!",
        completado: "¡Viaje completado! ¿Cómo estuvo tu experiencia?",
      };
      if (msgs[status]) toast.info(msgs[status]);
      if (status === "completado") setShowRating(true);
    });

    socket.on("transport:cancelled", () => {
      setActiveRide(null);
      setRyderInfo(null);
      setRyderPos(null);
      toast.info("El viaje fue cancelado");
    });

    return () => socket.disconnect();
  }, [token]);

  // Suscribirse a la sala del viaje cuando hay activeRide
  useEffect(() => {
    if (activeRide?.id && socketRef.current) {
      socketRef.current.emit("transport:subscribe", activeRide.id);
    }
  }, [activeRide?.id]);

  // ── Geolocalización del origen ────────────────────────────────────────────
  const getMyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      return toast.error("Tu navegador no soporta geolocalización");
    }
    toast.info("Obteniendo tu ubicación...", { toastId: "locating", autoClose: false });
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        toast.dismiss("locating");
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        try {
          const address = await reverseGeocode(lat, lng);
          setOrigin({ lat, lng, address });
        } catch {
          setOrigin({ lat, lng, address: `${lat.toFixed(5)}, ${lng.toFixed(5)}` });
        }
      },
      (err) => {
        toast.dismiss("locating");
        const msgs = {
          1: "Permiso denegado. Activa la ubicación en tu navegador.",
          2: "No se pudo determinar tu posición. Verifica tu GPS.",
          3: "Tiempo de espera agotado. Intenta de nuevo.",
        };
        toast.error(msgs[err.code] || "No se pudo obtener tu ubicación");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  }, []);

  // ── Buscar destino con Nominatim (sesgo: posición del usuario) ───────────
  useEffect(() => {
    if (destSearch.length < 3) { setDestSuggestions([]); return; }
    const timeout = setTimeout(async () => {
      const results = await geocodeAddress(destSearch, origin);
      setDestSuggestions(results.slice(0, 4));
    }, 500);
    return () => clearTimeout(timeout);
  }, [destSearch, origin]);

  // ── Cuando cambian origen y destino → pedir estimado ─────────────────────
  useEffect(() => {
    if (!origin || !dest) { setRouteCoords([]); setEstimate(null); return; }
    setLoadingEstimate(true);

    Promise.all([
      fetchRoute(origin, dest),
      API.get("/transport/estimate", {
        params: { originLat: origin.lat, originLng: origin.lng, destLat: dest.lat, destLng: dest.lng },
      }).then((r) => r.data?.data),
    ])
      .then(([coords, est]) => {
        setRouteCoords(coords);
        setEstimate(est);
      })
      .catch(() => toast.error("No se pudo calcular la ruta"))
      .finally(() => setLoadingEstimate(false));
  }, [origin, dest]);

  // ── Actualizar ruta del ryder cuando cambia su posición ──────────────────
  useEffect(() => {
    if (!ryderPos || !activeRide) return;
    const target = activeRide.status === "en_viaje"
      ? { lat: Number(activeRide.destLat), lng: Number(activeRide.destLng) }
      : { lat: Number(activeRide.originLat), lng: Number(activeRide.originLng) };
    fetchRoute(ryderPos, target).then(setRyderRouteCoords);
  }, [ryderPos, activeRide]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pedir viaje ───────────────────────────────────────────────────────────
  const handleRequestRide = async () => {
    if (!origin || !dest) return toast.error("Define tu origen y destino");
    setRequesting(true);
    try {
      const res = await API.post("/transport/", {
        originAddress: origin.address,
        originLat: origin.lat,
        originLng: origin.lng,
        destAddress: dest.address,
        destLat: dest.lat,
        destLng: dest.lng,
        paymentMethod,
        vehicleType,
      });
      setActiveRide(res.data?.data?.ride);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al solicitar el viaje");
    } finally {
      setRequesting(false);
    }
  };

  // ── Cancelar viaje ────────────────────────────────────────────────────────
  const handleCancel = async () => {
    if (!activeRide) return;
    try {
      await API.patch(`/transport/${activeRide.id}/status`, { status: "cancelado" });
      setActiveRide(null);
      setRyderInfo(null);
      setRyderPos(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "No se pudo cancelar el viaje");
    }
  };

  // ── Calificar viaje ───────────────────────────────────────────────────────
  const handleRate = async () => {
    try {
      await API.post(`/transport/${activeRide.id}/rate`, {
        rating: ratingValue,
        comment: ratingComment,
      });
      toast.success("¡Gracias por tu calificación!");
      setShowRating(false);
      setActiveRide(null);
      setRyderInfo(null);
      setRyderPos(null);
    } catch {
      toast.error("Error al enviar la calificación");
    }
  };

  // ── Vista: Mapa base (Venezuela como fallback mientras se obtiene GPS) ───
  const defaultCenter = origin
    ? [origin.lat, origin.lng]
    : [8.0, -66.0]; // Centro de Venezuela

  // ── RENDER ────────────────────────────────────────────────────────────────

  // Modal de calificación
  if (showRating && activeRide) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle className="text-green-500" size={28} />
            <h2 className="text-xl font-bold text-gray-800">¡Viaje completado!</h2>
          </div>
          <p className="text-gray-600 mb-4">
            Califica tu experiencia con{" "}
            <span className="font-semibold">{ryderInfo?.name}</span>
          </p>
          <StarRating value={ratingValue} onChange={setRatingValue} />
          <textarea
            className="mt-4 w-full border rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-supernova-400"
            placeholder="Comentario opcional..."
            rows={3}
            value={ratingComment}
            onChange={(e) => setRatingComment(e.target.value)}
          />
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => { setShowRating(false); setActiveRide(null); }}
              className="flex-1 py-2 rounded-xl border text-gray-600 hover:bg-gray-50 transition"
            >
              Omitir
            </button>
            <button
              onClick={handleRate}
              className="flex-1 py-2 rounded-xl bg-supernova-500 text-white font-bold hover:opacity-90 transition"
            >
              Enviar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Vista: Historial ─────────────────────────────────────────────────────
  if (tab === "historial") {
    return (
      <div className="flex flex-col bg-gray-50" style={{ height: "calc(100vh - 60px)" }}>
        {/* Header */}
        <div className="bg-white shadow-sm px-5 pt-4 pb-0 sticky top-0 z-10">
          <div className="flex items-center gap-3 mb-3">
            <Car size={22} className="text-supernova-500" />
            <h1 className="text-xl font-bold text-gray-800">Transporte</h1>
            {isRyder && (
              <button
                onClick={() => navigate("/transport/conductor")}
                className="ml-auto text-xs font-semibold text-supernova-600 bg-supernova-50 border border-supernova-200 px-3 py-1.5 rounded-full hover:bg-supernova-100 transition"
              >
                Panel conductor →
              </button>
            )}
          </div>
          {/* Tabs */}
          <div className="flex">
            {["solicitar", "historial"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`flex-1 py-2.5 text-sm font-semibold border-b-2 transition capitalize ${
                  tab === t
                    ? "border-supernova-500 text-supernova-600"
                    : "border-transparent text-gray-400 hover:text-gray-600"
                }`}
              >
                {t === "solicitar" ? "Solicitar viaje" : "Mis viajes"}
              </button>
            ))}
          </div>
        </div>

        {/* Lista */}
        <div className="flex-1 overflow-y-auto px-4 py-4 pb-28 space-y-3">
          {historyLoading && (
            <p className="text-center text-gray-400 py-10 animate-pulse">Cargando viajes...</p>
          )}
          {!historyLoading && history.length === 0 && (
            <div className="text-center py-16">
              <History size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">Aún no tienes viajes</p>
              <p className="text-gray-400 text-sm">Solicita tu primer viaje</p>
              <button
                onClick={() => setTab("solicitar")}
                className="mt-4 px-5 py-2.5 bg-supernova-500 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition"
              >
                Solicitar viaje
              </button>
            </div>
          )}
          {history.map((ride) => {
            const st = STATUS_CONFIG[ride.status] ?? { label: ride.status, color: "bg-gray-100 text-gray-600" };
            return (
              <div key={ride.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    {/* Origen → Destino */}
                    <div className="flex items-center gap-1.5 text-sm text-gray-600 mb-1">
                      <div className="w-2 h-2 rounded-full bg-supernova-400 shrink-0" />
                      <span className="truncate">{ride.originAddress}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <div className="w-2 h-2 rounded-sm bg-gray-400 shrink-0" />
                      <span className="truncate">{ride.destAddress}</span>
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-gray-300 shrink-0 mt-1" />
                </div>

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    {ride.ryder && (
                      <span className="flex items-center gap-1">
                        <VehicleIcon type={ride.ryder.vehicleType} size={13} />
                        {ride.ryder.user?.name}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {new Date(ride.createdAt).toLocaleDateString("es-CO", { day: "2-digit", month: "short" })}
                    </span>
                    <span>{Number(ride.distanceKm ?? 0).toFixed(1)} km</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${st.color}`}>
                      {st.label}
                    </span>
                    <span className="text-sm font-bold text-gray-700">
                      ${Number(ride.finalFare ?? ride.estimatedFare).toLocaleString("es-CO")}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <MobileBottomBar />
      </div>
    );
  }

  return (
    <div className="flex flex-col relative" style={{ height: "calc(100vh - 60px)" }}>
      {/* ── HEADER FLOTANTE SOBRE EL MAPA ── */}
      <div className="absolute top-0 left-0 right-0 z-20 pointer-events-none">
        <div className="bg-white/95 backdrop-blur-sm shadow-sm px-4 pt-3 pb-0 pointer-events-auto">
          <div className="flex items-center gap-2 mb-2">
            <Car size={20} className="text-supernova-500" />
            <span className="font-bold text-gray-800">Transporte</span>
            {isRyder && (
              <button
                onClick={() => navigate("/transport/conductor")}
                className="ml-auto text-xs font-semibold text-supernova-600 bg-supernova-50 border border-supernova-200 px-3 py-1.5 rounded-full hover:bg-supernova-100 transition"
              >
                Panel conductor →
              </button>
            )}
          </div>
          {/* Tabs */}
          <div className="flex">
            {["solicitar", "historial"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`flex-1 py-2 text-sm font-semibold border-b-2 transition ${
                  tab === t
                    ? "border-supernova-500 text-supernova-600"
                    : "border-transparent text-gray-400 hover:text-gray-600"
                }`}
              >
                {t === "solicitar" ? "Solicitar viaje" : "Mis viajes"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── MAPA ── */}
      <div className="flex-1 relative z-0" style={{ paddingTop: "88px" }}>
        <MapContainer
          center={defaultCenter}
          zoom={13}
          className="h-full w-full"
          zoomControl={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
          />

          {/* Seleccionar destino clickando el mapa (solo en vista A) */}
          {!activeRide && (
            <MapClickHandler
              onMapClick={async (latlng) => {
                const address = await reverseGeocode(latlng.lat, latlng.lng);
                setDest({ lat: latlng.lat, lng: latlng.lng, address });
                setDestSearch(address);
              }}
            />
          )}

          {origin && <Marker position={[origin.lat, origin.lng]} icon={originIcon} />}
          {dest    && <Marker position={[dest.lat, dest.lng]} icon={destIcon} />}
          {ryderPos && <Marker position={[ryderPos.lat, ryderPos.lng]} icon={ryderIcon} />}

          {/* Ruta del viaje planificada */}
          {routeCoords.length > 1 && !activeRide && (
            <Polyline positions={routeCoords} color="#f59e0b" weight={4} opacity={0.8} />
          )}

          {/* Ruta en tiempo real del ryder */}
          {ryderRouteCoords.length > 1 && (
            <Polyline positions={ryderRouteCoords} color="#7c3aed" weight={5} opacity={0.9} />
          )}

          {routeCoords.length > 1 && <MapFitter coords={routeCoords} />}
          {ryderRouteCoords.length > 1 && <MapFitter coords={ryderRouteCoords} />}
        </MapContainer>
      </div>

      {/* ── PANEL INFERIOR ── */}
      <div className="absolute bottom-16 lg:bottom-0 left-0 right-0 z-10">

        {/* ── VISTA A: Sin viaje activo — Solicitar viaje ── */}
        {!activeRide && (
          <div className="bg-white rounded-t-3xl shadow-2xl p-5 pb-8 space-y-4">
            <div className="w-10 h-1 bg-gray-300 rounded mx-auto mb-2" />
            <h2 className="text-xl font-bold text-gray-800">¿A dónde vas?</h2>

            {/* Tipo de vehículo */}
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Tipo de vehículo</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "moto",     label: "Moto",      icon: <Bike size={22} /> },
                  { key: "carro",    label: "Carro",     icon: <Car size={22} /> },
                  { key: "camioneta",label: "Camioneta", icon: <Truck size={22} /> },
                ].map(({ key, label, icon }) => (
                  <button
                    key={key}
                    onClick={() => setVehicleType(key)}
                    className={`flex flex-col items-center gap-1.5 py-3 rounded-2xl border-2 transition text-sm font-semibold ${
                      vehicleType === key
                        ? "border-supernova-500 bg-supernova-50 text-supernova-700"
                        : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    {icon}
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Origen */}
            <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
              <div className="w-3 h-3 rounded-full bg-supernova-500 shrink-0" />
              <span className="text-sm text-gray-700 flex-1 truncate">
                {origin?.address || "Obteniendo tu ubicación..."}
              </span>
              <button
                onClick={getMyLocation}
                className="text-supernova-500 hover:text-supernova-600 shrink-0"
                title="Usar mi ubicación actual"
              >
                <Navigation size={18} />
              </button>
            </div>

            {/* Destino */}
            <div className="relative">
              {/* Dropdown abre hacia ARRIBA para no empujar el layout */}
              {destSuggestions.length > 0 && (
                <div className="absolute bottom-full left-0 right-0 bg-white shadow-xl rounded-xl mb-1 z-30 max-h-52 overflow-y-auto border border-gray-100">
                  {destSuggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setDest({ lat: Number(s.lat), lng: Number(s.lon), address: s.display_name });
                        setDestSearch(s.display_name);
                        setDestSuggestions([]);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-supernova-50 text-sm border-b last:border-0"
                    >
                      <span className="line-clamp-2">{s.display_name}</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                <MapPin size={16} className="text-gray-400 shrink-0" />
                <input
                  type="text"
                  value={destSearch}
                  onChange={(e) => setDestSearch(e.target.value)}
                  placeholder="Buscar destino o haz clic en el mapa"
                  className="bg-transparent text-sm flex-1 outline-none placeholder-gray-400"
                />
                {dest && (
                  <button onClick={() => { setDest(null); setDestSearch(""); }} className="text-gray-400">
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* Estimado */}
            {loadingEstimate && (
              <p className="text-center text-sm text-gray-400 animate-pulse">Calculando ruta...</p>
            )}
            {estimate && !loadingEstimate && (
              <div className="bg-supernova-50 border border-supernova-200 rounded-xl p-4 flex justify-between items-center">
                <div className="flex items-center gap-2 text-gray-700">
                  <Car size={16} className="text-supernova-500" />
                  <span className="text-sm font-medium">{estimate.distanceKm} km</span>
                  <Clock size={16} className="text-supernova-500 ml-2" />
                  <span className="text-sm font-medium">~{estimate.durationMin} min</span>
                </div>
                <div className="flex items-center gap-1 text-supernova-600 font-bold text-lg">
                  <DollarSign size={16} />
                  {Number(estimate.estimatedFare).toLocaleString("es-CO")}
                </div>
              </div>
            )}

            {/* Método de pago */}
            {estimate && (
              <div className="flex gap-2 flex-wrap">
                {["efectivo", "nequi", "daviplata", "tarjeta"].map((m) => (
                  <button
                    key={m}
                    onClick={() => setPaymentMethod(m)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition capitalize ${
                      paymentMethod === m
                        ? "bg-supernova-500 text-white border-supernova-500"
                        : "bg-white text-gray-600 border-gray-300"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            )}

            {/* Botón */}
            <button
              onClick={handleRequestRide}
              disabled={!origin || !dest || !estimate || requesting}
              className="w-full py-4 rounded-2xl bg-supernova-500 text-white font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 active:scale-95 transition"
            >
              {requesting ? "Solicitando..." : "Pedir Viaje"}
            </button>
          </div>
        )}

        {/* ── VISTA B: Buscando conductor ── */}
        {activeRide?.status === "buscando" && (
          <div className="bg-white rounded-t-3xl shadow-2xl p-6 pb-8">
            <div className="text-center">
              {/* Animación de pulso */}
              <div className="relative mx-auto w-20 h-20 mb-4">
                <span className="absolute inset-0 rounded-full bg-supernova-200 animate-ping" />
                <span className="relative flex items-center justify-center w-20 h-20 rounded-full bg-supernova-500">
                  <Car size={32} className="text-white" />
                </span>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-1">Buscando conductor...</h3>
              <p className="text-gray-500 text-sm mb-2">
                {activeRide.originAddress} → {activeRide.destAddress}
              </p>
              <p className="text-supernova-600 font-semibold">
                ${Number(activeRide.estimatedFare).toLocaleString("es-CO")}
              </p>
            </div>
            <button
              onClick={handleCancel}
              className="mt-5 w-full py-3 rounded-xl border-2 border-red-400 text-red-500 font-semibold hover:bg-red-50 transition"
            >
              Cancelar solicitud
            </button>
          </div>
        )}

        {/* ── VISTA C: Viaje activo (aceptado / recogiendo / en_viaje) ── */}
        {activeRide && ["aceptado", "recogiendo", "en_viaje"].includes(activeRide.status) && (
          <div className="bg-white rounded-t-3xl shadow-2xl p-5 pb-8">
            {/* Estado del viaje */}
            <div className={`rounded-xl px-4 py-2 mb-4 text-center text-sm font-semibold ${
              activeRide.status === "en_viaje"
                ? "bg-green-100 text-green-700"
                : "bg-blue-100 text-blue-700"
            }`}>
              {activeRide.status === "aceptado"   && "Tu conductor va en camino a recogerte"}
              {activeRide.status === "recogiendo" && "Tu conductor llegó al punto de recogida"}
              {activeRide.status === "en_viaje"   && "¡Estás en camino a tu destino!"}
            </div>

            {/* Info del conductor */}
            {ryderInfo && (
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-supernova-100 flex items-center justify-center text-2xl shrink-0">
                  {ryderInfo.avatar ? (
                    <img src={ryderInfo.avatar} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : "🚗"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-800 truncate">{ryderInfo.name}</p>
                  <p className="text-sm text-gray-500 capitalize">{ryderInfo.vehicleType} • {ryderInfo.vehiclePlate || "—"}</p>
                  <div className="flex items-center gap-1 text-xs text-yellow-500">
                    <Star size={12} fill="currentColor" />
                    <span>{Number(ryderInfo.rating).toFixed(1)}</span>
                  </div>
                </div>
                {ryderInfo.phone && (
                  <a
                    href={`tel:${ryderInfo.phone}`}
                    className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white hover:bg-green-600 transition shrink-0"
                  >
                    <Phone size={18} />
                  </a>
                )}
              </div>
            )}

            {/* Cancelar — solo en aceptado */}
            {activeRide.status === "aceptado" && (
              <button
                onClick={handleCancel}
                className="mt-4 w-full py-3 rounded-xl border border-red-300 text-red-500 text-sm font-semibold hover:bg-red-50 transition"
              >
                Cancelar viaje
              </button>
            )}
          </div>
        )}
      </div>
      <MobileBottomBar />
    </div>
  );
}
